import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  Send,
  Paperclip,
} from 'lucide-react';
import {
  Card,
  Badge,
  Input,
  Textarea,
  Select,
  Button,
  ShimmerButton,
  ResponsiveDialog,
  Tabs,
  EmptyState,
  useToast,
  useViewport,
  cn,
  formatDate,
  formatTime,
  staggerChildren,
  fadeInUp,
} from '@raffle/ui';

interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high';
  createdAt: Date;
  updatedAt: Date;
  messages: Array<{
    id: string;
    content: string;
    isStaff: boolean;
    createdAt: Date;
  }>;
}

// Mock data
const mockTickets: SupportTicket[] = [
  {
    id: '1',
    subject: 'Payment not reflecting',
    category: 'payment',
    status: 'in-progress',
    priority: 'high',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-16'),
    messages: [
      {
        id: 'm1',
        content: 'I made a payment for tickets but they are not showing in my account.',
        isStaff: false,
        createdAt: new Date('2024-01-15'),
      },
      {
        id: 'm2',
        content: 'Thank you for reaching out. We\'re looking into this and will update you shortly.',
        isStaff: true,
        createdAt: new Date('2024-01-16'),
      },
    ],
  },
  {
    id: '2',
    subject: 'How do I claim my prize?',
    category: 'general',
    status: 'resolved',
    priority: 'medium',
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-12'),
    messages: [
      {
        id: 'm3',
        content: 'I won a raffle! How do I claim my property?',
        isStaff: false,
        createdAt: new Date('2024-01-10'),
      },
      {
        id: 'm4',
        content: 'Congratulations! Our team will contact you within 48 hours to guide you through the claim process.',
        isStaff: true,
        createdAt: new Date('2024-01-12'),
      },
    ],
  },
];

type TicketStatus = 'all' | 'open' | 'in-progress' | 'resolved';

export const SupportPage: React.FC = () => {
  const { isDesktop, isMobile } = useViewport();
  const toast = useToast();

  const MessageSquareIcon = ({ className }: { className?: string }) => (
    <MessageSquare className={className} />
  );

  const [tickets] = useState<SupportTicket[]>(mockTickets);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TicketStatus>('all');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    message: '',
  });

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!ticket.subject.toLowerCase().includes(query)) return false;
      }

      if (statusFilter !== 'all') {
        if (statusFilter === 'open' && ticket.status !== 'open') return false;
        if (statusFilter === 'in-progress' && ticket.status !== 'in-progress') return false;
        if (statusFilter === 'resolved' && !['resolved', 'closed'].includes(ticket.status)) return false;
      }

      return true;
    });
  }, [tickets, searchQuery, statusFilter]);

  const selectedTicket = selectedTicketId
    ? tickets.find((t) => t.id === selectedTicketId)
    : null;

  const stats = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in-progress').length,
    resolved: tickets.filter((t) => ['resolved', 'closed'].includes(t.status)).length,
  }), [tickets]);

  const statusTabs = [
    { value: 'all' as const, label: 'All', count: stats.total },
    { value: 'open' as const, label: 'Open', count: stats.open },
    { value: 'in-progress' as const, label: 'In Progress', count: stats.inProgress },
    { value: 'resolved' as const, label: 'Resolved', count: stats.resolved },
  ];

  const statusConfig = {
    open: { label: 'Open', variant: 'warning' as const, icon: Clock },
    'in-progress': { label: 'In Progress', variant: 'default' as const, icon: Clock },
    resolved: { label: 'Resolved', variant: 'success' as const, icon: CheckCircle },
    closed: { label: 'Closed', variant: 'default' as const, icon: CheckCircle },
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error('Missing Information', 'Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setShowNewTicket(false);
    setNewTicket({ subject: '', category: 'general', message: '' });
    toast.success('Ticket Created', 'Your support ticket has been submitted');
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setReplyText('');
    toast.success('Reply Sent', 'Your message has been sent');
  };

  return (
    <div className="page-container">
      <section className="content-wrapper py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-text-primary mb-2">
              Support Center
            </h1>
            <p className="text-text-secondary">
              Get help with your account and raffles
            </p>
          </div>
          <ShimmerButton onClick={() => setShowNewTicket(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </ShimmerButton>
        </div>

        {/* Quick Help Cards */}
        <motion.div
          className={cn('grid gap-4 mb-8', isDesktop ? 'grid-cols-3' : 'grid-cols-1')}
          variants={staggerChildren}
          initial="hidden"
          animate="show"
        >
          {[
            {
              icon: MessageSquare,
              title: 'FAQ',
              description: 'Find answers to common questions',
              action: 'Browse FAQ',
            },
            {
              icon: AlertCircle,
              title: 'Payment Issues',
              description: 'Having trouble with payments?',
              action: 'Get Help',
            },
            {
              icon: CheckCircle,
              title: 'Claim Guide',
              description: 'How to claim your winnings',
              action: 'Learn More',
            },
          ].map((item, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <Card className="p-4 cursor-pointer hover:border-gold-500/30 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-vault bg-gold-500/10">
                    <item.icon className="w-5 h-5 text-gold-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-text-primary mb-1">{item.title}</h3>
                    <p className="text-sm text-text-muted mb-2">{item.description}</p>
                    <span className="text-sm text-gold-500">{item.action} →</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* My Tickets Section */}
        <div className="mb-6">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
            My Tickets
          </h2>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="search"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
            />
          </div>
          
          {isMobile && (
            <Button variant="outline" onClick={() => setShowFilters(true)}>
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          )}
        </div>

        {/* Status Tabs */}
        <Tabs
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as TicketStatus)}
          tabs={statusTabs.map((tab) => ({
            value: tab.value,
            label: tab.label,
            badge: tab.count,
          }))}
          className="mb-6"
        />

        {/* Tickets List */}
        {filteredTickets.length === 0 ? (
          <EmptyState
            icon={MessageSquareIcon}
            title="No tickets found"
            description={
              searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : "You haven't created any support tickets yet"
            }
            action={
              searchQuery || statusFilter !== 'all'
                ? {
                    label: 'Clear Filters',
                    onClick: () => {
                      setSearchQuery('');
                      setStatusFilter('all');
                    },
                  }
                : {
                    label: 'Create Ticket',
                    onClick: () => setShowNewTicket(true),
                  }
            }
          />
        ) : (
          <motion.div
            className="space-y-3"
            variants={staggerChildren}
            initial="hidden"
            animate="show"
          >
            {filteredTickets.map((ticket) => {
              const status = statusConfig[ticket.status];
              const StatusIcon = status.icon;

              return (
                <motion.div key={ticket.id} variants={fadeInUp}>
                  <Card
                    className="p-4 cursor-pointer hover:border-gold-500/30 transition-colors"
                    onClick={() => setSelectedTicketId(ticket.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={status.variant}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                          <span className="text-xs text-text-muted">
                            #{ticket.id}
                          </span>
                        </div>
                        <h3 className="font-medium text-text-primary truncate">
                          {ticket.subject}
                        </h3>
                        <p className="text-sm text-text-muted">
                          Updated {formatDate(ticket.updatedAt)}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </section>

      {/* New Ticket Modal */}
      <ResponsiveDialog
        isOpen={showNewTicket}
        onClose={() => !isSubmitting && setShowNewTicket(false)}
        title="Create Support Ticket"
      >
        <div className="p-6 space-y-6">
          <Input
            label="Subject"
            value={newTicket.subject}
            onChange={(e) => setNewTicket((prev) => ({ ...prev, subject: e.target.value }))}
            placeholder="Brief description of your issue"
          />

          <Select
            label="Category"
            value={newTicket.category}
            onChange={(e) =>
              setNewTicket((prev) => ({ ...prev, category: e.currentTarget.value }))
            }
            options={[
              { value: 'general', label: 'General Inquiry' },
              { value: 'payment', label: 'Payment Issue' },
              { value: 'technical', label: 'Technical Problem' },
              { value: 'raffle', label: 'Raffle Question' },
              { value: 'claim', label: 'Prize Claim' },
            ]}
          />

          <Textarea
            label="Message"
            value={newTicket.message}
            onChange={(e) => setNewTicket((prev) => ({ ...prev, message: e.target.value }))}
            placeholder="Describe your issue in detail..."
            rows={5}
          />

          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowNewTicket(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <ShimmerButton fullWidth onClick={handleCreateTicket} loading={isSubmitting}>
              Submit Ticket
            </ShimmerButton>
          </div>
        </div>
      </ResponsiveDialog>

      {/* Ticket Detail Modal */}
      <ResponsiveDialog
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicketId(null)}
        title={`Ticket #${selectedTicket?.id}`}
      >
        {selectedTicket && (
          <div className="flex flex-col h-full max-h-[70vh]">
            {/* Header */}
            <div className="p-4 border-b border-white/5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Badge variant={statusConfig[selectedTicket.status].variant} className="mb-2">
                    {statusConfig[selectedTicket.status].label}
                  </Badge>
                  <h3 className="font-display font-semibold text-text-primary">
                    {selectedTicket.subject}
                  </h3>
                  <p className="text-sm text-text-muted">
                    Created {formatDate(selectedTicket.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selectedTicket.messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex',
                    message.isStaff ? 'justify-start' : 'justify-end'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[80%] p-3 rounded-vault-lg',
                      message.isStaff
                        ? 'bg-surface-2 border border-white/5'
                        : 'bg-gold-500/10 border border-gold-500/20'
                    )}
                  >
                    <p className="text-sm text-text-primary">{message.content}</p>
                    <p className="text-xs text-text-muted mt-2">
                      {message.isStaff ? 'Support Team' : 'You'} • {formatTime(message.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Input */}
            {!['resolved', 'closed'].includes(selectedTicket.status) && (
              <div className="p-4 border-t border-white/5">
                <div className="flex gap-2">
                  <button className="p-2 rounded-vault bg-surface-2 text-text-muted hover:text-text-secondary transition-colors">
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply();
                      }
                    }}
                  />
                  <Button
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || isSubmitting}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </ResponsiveDialog>

      {/* Mobile Filter Dialog */}
      <ResponsiveDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filter Tickets"
      >
        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-medium text-text-primary">Status</p>
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => {
                  setStatusFilter(tab.value);
                  setShowFilters(false);
                }}
                className={cn(
                  'w-full flex items-center justify-between p-3 rounded-vault border transition-colors',
                  statusFilter === tab.value
                    ? 'bg-gold-500/10 border-gold-500/30 text-gold-500'
                    : 'bg-surface-2/50 border-white/5 text-text-secondary hover:border-white/10'
                )}
              >
                <span>{tab.label}</span>
                <span className="font-mono text-sm tabular-nums">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
